// AiSurfaceMessage.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Context
import { useCurrentLanguage } from 'context/LanguageContext';
import { useNodeTypes } from 'context/NodeTypesContext';
import { useTheme } from 'context/ThemeContext';
import { useUser } from 'context/UserContext';
import { useBase } from 'context/BaseContext';

// Utilities
import { formatChannelRelativeTime } from 'channels/utils/channelTime';
import { buildPersonalAgendaEventItems } from 'spaces/utils/buildPersonalAgendaEventItems';
import { normalizeSurfaceNode, normalizeSurfaceNodes } from 'spaces/utils/normalizeSurfaceNodes';
import { resolveClientScheduleWindow, resolveClientTimelineWindow, planRequestsOverdueSchedule } from 'spaces/utils/scheduleWindow';
import { resolveSchemaFieldValue } from 'views/utils/fieldValueResolver';
import { createFilterSortEvaluator } from 'views/utils/filterSortEvaluator';

// Services
import { loadBaseCalendarEventItems } from 'services/events/BaseCalendarEventService';
import { syncNodeTypeSchemaCacheForBase } from 'services/nodetypes/NodeTypesCacheService';

// Hooks
import useSurfaceNodes from 'spaces/hooks/useSurfaceNodes';

// Constants
import { AI_TITLE } from 'spaces/constants/aiConstants';

// CSS
import './AiSurfaceMessage.css';

// Components
import Button from 'components/button/Button';
import PlanesMark from 'components/logo/mark/PlanesMark';
import { db } from 'firebaseConfig';
import AiCalendarTimelineProvider from 'spaces/components/AiCalendarTimelineProvider';
import AiFormattedText from 'spaces/components/AiFormattedText';
import AiLocationDetails from 'spaces/components/AiLocationDetails';
import AiMessageActions from 'spaces/components/AiMessageActions';
import AiMultiTimelineSurface from 'spaces/components/AiMultiTimelineSurface';
import AiThreadChrome from 'spaces/components/AiThreadChrome';
import ViewRenderer from 'views/ViewRenderer';

const AiSurfaceMessage = React.memo(({
  message,
  promptText = '',
  canReplyInThread = false,
  hasUnreadReplies = false,
  onOpenThread,
  onReplyInThread,
  threadSummariesByRootKey = {},
}) => {
  // ----- CONTEXT ----- //

  const { theme } = useTheme();
  const language = useCurrentLanguage();
  const { getType, getRevision } = useNodeTypes();
  const { user } = useUser();
  const { activeBase } = useBase();
  // ----- STATE ----- //

  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [agendaEventItems, setAgendaEventItems] = useState(undefined);
  const [countExpanded, setCountExpanded] = useState(false);

  const surface = message?.surface && typeof message.surface === 'object' ? message.surface : null;
  const plan = surface?.plan && typeof surface.plan === 'object' ? surface.plan : null;
  const typeId = String(surface?.typeId || plan?.query?.typeId || '').trim();
  const view = plan?.presentation?.view || null;
  const rawNodes = Array.isArray(surface?.nodes) ? surface.nodes : [];
  // ----- HOOKS ----- //

  const { nodes: sourceNodes } = useSurfaceNodes(rawNodes, {
    enabled: rawNodes.length > 0,
  });
  const createdLabel = formatChannelRelativeTime(message?.dateCreated);
  const isPersonalAgenda = Boolean(
    surface?.meta?.personalAgenda
    || plan?.query?.personalAgenda
    || plan?.presentation?.view?.personalAgenda,
  );

  const isDueCalendar = Boolean(
    surface?.meta?.dueCalendar
    || plan?.query?.dueCalendar
    || plan?.presentation?.view?.dueCalendar,
  );
  const isGlobalAgenda = Boolean(
    surface?.meta?.globalAgenda
    || plan?.query?.globalAgenda
    || plan?.presentation?.view?.globalAgenda,
  );
  const isMultiTypeCalendar = isPersonalAgenda || isDueCalendar || isGlobalAgenda;
  const isMultiTimeline = Boolean(
    surface?.meta?.multiTimeline
    || plan?.query?.multiTimeline
    || plan?.presentation?.view?.multiTimeline,
  );
  const isRecordSearch = Boolean(
    surface?.meta?.recordSearch
    || plan?.query?.recordSearch
    || plan?.presentation?.view?.recordSearch,
  );
  const isRecordLocationRead = Boolean(
    surface?.meta?.recordLocationRead
    || plan?.query?.locationRead
    || plan?.presentation?.view?.recordLocationRead,
  );
  const isRecordUpdate = Boolean(surface?.meta?.recordUpdate || plan?.query?.recordUpdate);
  const isRecordCount = Boolean(surface?.meta?.recordCount);
  const showCountSurface = !isRecordCount || countExpanded;
  const locationReadDetails = surface?.locationRead && typeof surface.locationRead === 'object'
    ? surface.locationRead
    : null;
  const locationReadEntries = Array.isArray(locationReadDetails?.entries)
    ? locationReadDetails.entries
    : [];
  const showLocationReadDetails = isRecordLocationRead && locationReadEntries.length > 0;

  const nodeTypesRevision = getRevision();

  // ----- MEMOS ----- //

  const surfaceTypeDef = useMemo(() => {
    void nodeTypesRevision;
    const nodeTypeKey = sourceNodes.length > 0 ? String(sourceNodes[0]?.type || '').trim() : '';
    if (typeId) {
      return getType(typeId) || (nodeTypeKey ? getType(nodeTypeKey) : null);
    }
    if ((isMultiTypeCalendar || isRecordSearch || isRecordUpdate) && nodeTypeKey) {
      return getType(nodeTypeKey) || null;
    }
    if (isRecordLocationRead && nodeTypeKey) {
      return getType(nodeTypeKey) || null;
    }
    return null;
  }, [getType, isMultiTypeCalendar, isRecordLocationRead, isRecordSearch, isRecordUpdate, nodeTypesRevision, sourceNodes, typeId]);

  const schemaFieldMap = useMemo(() => {
    const map = new Map();
    const fields = Array.isArray(surfaceTypeDef?.properties)
      ? surfaceTypeDef.properties
      : (Array.isArray(surfaceTypeDef?.schema?.fields) ? surfaceTypeDef.schema.fields : []);

    fields.forEach((field) => {
      const fieldId = String(field?.name || field?.key || field?.fieldId || '').trim();
      if (fieldId) map.set(fieldId, field);
    });
    return map;
  }, [surfaceTypeDef]);

  const { applyFilters, applySorts } = useMemo(() => {
    const resolveFieldValue = (item, fieldDef) => resolveSchemaFieldValue({
      item,
      fieldDef,
      typeKey: typeId || item?.type,
    });

    return createFilterSortEvaluator({
      resolveFieldValue,
      schemaFieldMap,
      viewerUserKey: user?.key,
    });
  }, [schemaFieldMap, typeId, user?.key]);

  const nodes = useMemo(() => {
    const viewFilters = Array.isArray(view?.filters) ? view.filters : [];
    const viewSorts = Array.isArray(view?.sorts) ? view.sorts : [];
    const hydratedNodes = isMultiTypeCalendar || isRecordSearch || isRecordUpdate
      ? sourceNodes.map((node) => normalizeSurfaceNode(node, getType(node?.type)))
      : normalizeSurfaceNodes(sourceNodes, surfaceTypeDef);
    const filtered = viewFilters.length ? hydratedNodes.filter((row) => applyFilters(row, viewFilters)) : hydratedNodes;
    return viewSorts.length ? applySorts(filtered, viewSorts) : filtered;
  }, [applyFilters, applySorts, getType, isMultiTypeCalendar, isRecordSearch, isRecordUpdate, sourceNodes, surfaceTypeDef, view?.filters, view?.sorts]);

  const viewType = String(view?.type || '').trim().toUpperCase();

  const resolvedScheduleWindow = useMemo(() => {
    if (viewType === 'TIMELINE') {
      return plan?.query?.scheduleWindow || resolveClientTimelineWindow(plan);
    }
    if (viewType === 'CALENDAR') {
      return plan?.query?.scheduleWindow || resolveClientScheduleWindow(plan);
    }
    return null;
  }, [plan, viewType]);

  const ephemeralView = useMemo(() => {
    if (!view) return null;

    return {
      ...view,
      ...(resolvedScheduleWindow ? { scheduleWindow: resolvedScheduleWindow } : {}),
      key: `ephemeral_${surface?.analysisId || message?.key || 'surface'}`,
    };
  }, [message?.key, resolvedScheduleWindow, surface?.analysisId, view]);

  const calendarSubView = String(ephemeralView?.calendarSubView || 'AGENDA').trim().toUpperCase();
  const displayMode = viewType === 'CALENDAR' && calendarSubView === 'AGENDA' ? 'compact' : 'full';
  const isOverdueSchedule = planRequestsOverdueSchedule(plan);
  const usesCalendarTimelineProvider = viewType === 'CALENDAR' || viewType === 'TIMELINE';
  const usesEventsBackedCalendar = isMultiTypeCalendar && viewType === 'CALENDAR' && !isOverdueSchedule;
  const scheduleWindow = resolvedScheduleWindow;
  const baseKey = String(activeBase?.key || plan?.scope?.baseId || '').trim();
  const renderTypeDef = (usesEventsBackedCalendar || isOverdueSchedule || (isRecordSearch && !isRecordLocationRead))
    ? null
    : surfaceTypeDef;

  // ----- EFFECTS ----- //

  useEffect(() => {
    if (!usesEventsBackedCalendar || !baseKey || !scheduleWindow) {
      setAgendaEventItems(undefined);
      return undefined;
    }

    let cancelled = false;
    const allowedNodeKeys = new Set(
      sourceNodes
        .map((node) => String(node?.key || node?.id || '').trim())
        .filter(Boolean),
    );

    loadBaseCalendarEventItems({
      baseKey,
      windowStartMs: scheduleWindow.windowStartMs,
      windowEndMs: scheduleWindow.windowEndMs,
    })
      .then((items) => {
        if (cancelled) return;
        const filtered = (Array.isArray(items) ? items : []).filter((item) => (
          allowedNodeKeys.has(String(item?.node?.key || '').trim())
        ));
        setAgendaEventItems(filtered);
      })
      .catch(() => {
        if (!cancelled) setAgendaEventItems([]);
      });

    return () => {
      cancelled = true;
    };
  }, [
    sourceNodes,
    scheduleWindow,
    usesEventsBackedCalendar,
    baseKey,
  ]);

  // ----- MEMOS ----- //

  const calendarEventItems = useMemo(() => {
    if (!usesEventsBackedCalendar && !isOverdueSchedule) return undefined;
    if (Array.isArray(agendaEventItems) && agendaEventItems.length > 0) return agendaEventItems;
    return buildPersonalAgendaEventItems(sourceNodes, getType);
  }, [agendaEventItems, getType, isOverdueSchedule, sourceNodes, usesEventsBackedCalendar]);

  const canRenderSurface = Boolean(
    ephemeralView && (renderTypeDef || usesEventsBackedCalendar || isOverdueSchedule || isMultiTimeline || isRecordSearch || isRecordUpdate || isRecordLocationRead),
  );

  const analysisKey = surface?.analysisId || message?.key || 'surface';

  // ----- EFFECTS ----- //

  useEffect(() => {
    if (!isRecordUpdate || !baseKey) return undefined;

    const typeKey = String(typeId || sourceNodes[0]?.type || '').trim();
    if (!typeKey) return undefined;

    syncNodeTypeSchemaCacheForBase(db, {
      baseKey,
      typeKey,
      subscribe: false,
      ttlMs: 0,
    }).catch(() => {});

    return undefined;
  }, [analysisKey, isRecordUpdate, sourceNodes, typeId, baseKey]);

  const multiTimelineContent = isMultiTimeline ? (
    <AiMultiTimelineSurface
      analysisKey={analysisKey}
      getType={getType}
      language={language}
      plan={plan}
      surface={surface}
      theme={theme}
    />
  ) : null;

  const surfaceContent = isMultiTimeline ? multiTimelineContent : canRenderSurface ? (
    nodes.length > 0 ? (
      <ViewRenderer
        displayMode={displayMode}
        enforceExplicitProperties={!isRecordSearch || isRecordLocationRead}
        eventItems={calendarEventItems}
        interactionMode="surface"
        language={language}
        nodes={nodes}
        selectedKeys={selectedKeys}
        setSelectedKeys={setSelectedKeys}
        tableShowCheckboxes={false}
        typeDef={renderTypeDef}
        view={ephemeralView}
      />
    ) : (
      <div
        className="ai-surface-message__empty"
      >
        {rawNodes.length > 0
          ? 'No matching records after applying this view.'
          : `No matching records${view?.type ? ` for this ${String(view.type).toLowerCase()} view` : ''}.`}
      </div>
    )
  ) : (
    <div
      className="ai-surface-message__empty"
    >
      Surface unavailable.
    </div>
  );

  // ----- RENDER ----- //

  return (
    <div className="ai-surface-message" id={`message-${message?.key || ''}`}>

      {/* ORBITAL AI MESSAGE ROW */}
      <div
        className="ai-message-row"
        style={{
          padding: 0,
        }}
      >

        {/* AVATAR */}
        <div
          className="ai-message-row__avatar"
          aria-hidden
        >
          <PlanesMark size={32} variant="brand" />
        </div>

        {/* BODY */}
        <div className="ai-message-row__body">

          {/* HEADER */}
          <div className="ai-message-row__header">

            {/* NAME */}
            <span
              className="ai-message-row__name"
            >
              {AI_TITLE}
            </span>

            {/* TIME */}
            {createdLabel ? (
              <span
                className="ai-message-row__time"
              >
                {createdLabel}
              </span>
            ) : null}
          </div>

          {showLocationReadDetails ? (
            <div className="ai-surface-message__narrative">
              <AiLocationDetails
                entries={locationReadEntries}
                nodeTitle={locationReadDetails?.nodeTitle || ''}
              />
              <AiMessageActions message={message} surfaceNodes={sourceNodes} />
            </div>
          ) : message?.text ? (
            <div
              className="ai-surface-message__narrative"
            >
              <AiFormattedText text={message.text} keyPrefix="narrative" />

              {/* ACTION */}
              {isRecordCount && !countExpanded && rawNodes.length > 0 ? (
                <div className="ai-message-row__action">
                  <Button
                    label="View"
                    iconKey="visibility"
                    width="fit-content"
                    onClick={() => setCountExpanded(true)}
                  />
                </div>
              ) : null}
              {!isRecordCount ? (
                <AiMessageActions message={message} surfaceNodes={sourceNodes} />
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {/* SURFACE */}
      {showCountSurface ? (
        <div
          className="ai-surface-message__surface"
        >
          {usesCalendarTimelineProvider && !isMultiTimeline ? (
            <AiCalendarTimelineProvider view={ephemeralView}>
              {surfaceContent}
            </AiCalendarTimelineProvider>
          ) : surfaceContent}
        </div>
      ) : null}

      <AiThreadChrome
        message={message}
        promptText={promptText}
        canReplyInThread={canReplyInThread}
        hasUnreadReplies={hasUnreadReplies}
        threadSummariesByRootKey={threadSummariesByRootKey}
        onOpenThread={onOpenThread}
        onReplyInThread={onReplyInThread}
      />
    </div>
  );
});

// Dev Tools
AiSurfaceMessage.displayName = 'AiSurfaceMessage';

export default AiSurfaceMessage;
